import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
    ContainerOuterStyled,
    ControlContainerStyled,
    ValuesContainerStyled,
    PlaceholderContainerStyled,
    CurrentSelectionContainerStyled,
    CurrentSelectionLabelStyled,
    InputContainerStyled,
    InputStyled,
    IndicatorContainerStyled,
    IndicatorDividerStyled,
    IndicatorIconContainerStyled,
    MenuContainerStyled,
    MenuInnerStyled,
    CurrentSelectionButtonStyled,
} from './SelectShellStyled';

import useClickOutside from '@hooks/useClickOutside';

const SelectShell = ({
    children,
    inputValue,
    placeholder = '',
    currentSelections = [],
    onCurrentSelectionRemove,
    onInputChange,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const clickOutsideRef = useRef(null);
    const inputRef = useRef(null);

    useClickOutside({ ref: clickOutsideRef, onClickOutside: () => closeMenu() });

    const toggleMenuOpen = () => {
        if (!isOpen) {
            inputRef.current.focus();
            setIsOpen(true);
        } else {
            closeMenu();
        }
    };

    const closeMenu = () => {
        setIsOpen(false);
        onInputChange('');
    };

    return (
        <ContainerOuterStyled ref={clickOutsideRef}>
            <ControlContainerStyled
                className={classnames({ open: isOpen })}
                onClick={e => {
                    if (e.target.closest('.currentSelectionIcon')) {
                        return;
                    }

                    toggleMenuOpen();
                }}
            >
                <ValuesContainerStyled>
                    {inputValue.length === 0 && currentSelections.length === 0 && (
                        <PlaceholderContainerStyled>{placeholder}</PlaceholderContainerStyled>
                    )}

                    {currentSelections.map(selection => (
                        <CurrentSelectionContainerStyled key={selection.value}>
                            <CurrentSelectionLabelStyled>{selection.label}</CurrentSelectionLabelStyled>

                            <CurrentSelectionButtonStyled
                                className="currentSelectionIcon"
                                onClick={() => onCurrentSelectionRemove(selection)}
                            >
                                <svg height="14" width="14" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                    <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
                                </svg>
                            </CurrentSelectionButtonStyled>
                        </CurrentSelectionContainerStyled>
                    ))}

                    <InputContainerStyled>
                        <div className="d-inline-block">
                            <InputStyled
                                autoCapitalize="none"
                                autoComplete="off"
                                autoCorrect="off"
                                ref={inputRef}
                                spellCheck="false"
                                tabIndex="0"
                                type="text"
                                aria-autocomplete="list"
                                value={inputValue}
                                style={{
                                    width: inputValue.length === 0 ? '2px' : '100%',
                                }}
                                onChange={e => onInputChange(e.target.value)}
                            />
                        </div>
                    </InputContainerStyled>
                </ValuesContainerStyled>

                <IndicatorContainerStyled>
                    <IndicatorDividerStyled />

                    <IndicatorIconContainerStyled>
                        <svg height="20" width="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                            <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
                        </svg>
                    </IndicatorIconContainerStyled>
                </IndicatorContainerStyled>
            </ControlContainerStyled>

            {isOpen && (
                <MenuContainerStyled>
                    <MenuInnerStyled onClick={() => closeMenu()}>{children}</MenuInnerStyled>
                </MenuContainerStyled>
            )}
        </ContainerOuterStyled>
    );
};

SelectShell.propTypes = {
    children: PropTypes.any,
    currentSelections: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ),
    inputValue: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    onCurrentSelectionRemove: PropTypes.func,
    onInputChange: PropTypes.func.isRequired,
};

export default SelectShell;
